/**
 * Auth hook for managing user authentication state and role-based access
 * Uses React Query for efficient caching and shared state across components.
 *
 * Waits for AuthProvider to finish bootstrapping the token from storage before
 * issuing the first profile request, otherwise the call would race the
 * synchronous token cache and go out without an Authorization header.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient from "@/lib/api/api-client"
import { useAuthSession } from "@/lib/auth/auth-provider"
import type { User, UserRole } from "@/types/user-types"

/** Query key for user profile - exported for cache invalidation */
export const authKeys = {
  user: ["auth", "user"] as const,
}

interface AuthResult {
  user: User | null
  loading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Main auth hook - fetches and caches user profile
 * Uses React Query to ensure single API call across all components
 */
export function useAuth(): AuthResult {
  const { isReady, hasToken } = useAuthSession()

  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: authKeys.user,
    queryFn: async () => {
      if (!hasToken) return null
      try {
        return await apiClient.users.getProfile()
      } catch {
        // Return null for auth errors (user not logged in)
        // This prevents error state from triggering for expected scenarios
        return null
      }
    },
    enabled: isReady,
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: false, // Don't retry auth failures
  })

  return {
    user: user ?? null,
    loading: !isReady || isLoading || isFetching,
    error: error ? (error as Error).message : null,
    refetch: () => { refetch() },
  }
}

/**
 * Hook to invalidate auth cache (call after login/logout)
 */
export function useInvalidateAuth() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: authKeys.user })
}

/**
 * Get the current user's role
 */
export function useUserRole(): UserRole | null {
  const { user } = useAuth()
  return user?.role ?? null
}

/**
 * Check if user is admin
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth()
  return user?.role === "admin"
}

/**
 * Check if user can manage users (admin only)
 */
export function useCanManageUsers(): boolean {
  const { user } = useAuth()
  return user?.role === "admin"
}

/**
 * Get the current user 
 */
export function useCurrentUser(): User | null {
  const { user } = useAuth()
  return user
}
